import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/d74h3havu4krcbn3b5l0";

const TOPICS = [
  "機能の不具合・エラー",
  "表示がおかしい",
  "支払い・決済のトラブル",
  "アカウントに関する問題",
  "コンテンツについての問題",
  "その他のご意見・ご要望",
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const charCount = form.message.length;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        topic: form.topic,
        message: form.message,
      });
      await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-deep-green py-16 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-display italic text-gold text-xs tracking-widest mb-3">Contact & Report</p>
            <h1 className="font-serif text-white text-3xl md:text-4xl font-light mb-4">お問い合わせ・不備の報告</h1>
            <p className="text-white/60 text-sm font-sans leading-relaxed">
              ご利用中に気になる点や不具合を発見した際は、こちらのフォームからお知らせください。<br className="hidden md:block" />
              内容を確認のうえ、担当者よりご連絡いたします。
            </p>
          </div>
        </section>

        <section className="py-16 px-4">
          <div className="max-w-2xl mx-auto">
            {submitted ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-stone-100">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-teal/10 mx-auto mb-6">
                  <i className="ri-checkbox-circle-fill text-5xl text-teal" />
                </div>
                <h2 className="font-serif text-deep-green text-2xl mb-3">送信完了しました</h2>
                <p className="text-charcoal/60 font-sans text-sm leading-relaxed mb-8">
                  お問い合わせありがとうございます。<br />
                  内容を確認後、担当者よりご連絡差し上げます。
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-8 py-3 bg-deep-green text-white rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap"
                >
                  別の問い合わせをする
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-stone-100 overflow-hidden">
                <div className="px-8 py-6 border-b border-stone-100">
                  <h2 className="font-serif text-deep-green text-xl">お問い合わせフォーム</h2>
                  <p className="text-xs text-charcoal/50 font-sans mt-1">
                    ※メールアドレスは公開されません。担当者からの返信にのみ使用します。
                  </p>
                </div>
                <form
                  data-readdy-form
                  id="contact-report-form"
                  onSubmit={handleSubmit}
                  className="p-8 space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-sans text-charcoal/60 mb-1.5">お名前 <span className="text-red-400">*</span></label>
                      <input
                        name="name"
                        required
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="山田 太郎"
                        className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-sans text-charcoal/60 mb-1.5">メールアドレス <span className="text-red-400">*</span></label>
                      <input
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="your@email.com"
                        className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1.5">問い合わせ種別 <span className="text-red-400">*</span></label>
                    <select
                      name="topic"
                      required
                      value={form.topic}
                      onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal bg-white cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1.5">内容の詳細 <span className="text-red-400">*</span></label>
                    <textarea
                      name="message"
                      required
                      value={form.message}
                      onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                      maxLength={500}
                      rows={6}
                      placeholder="問い合わせ内容や発生している問題を詳しくご記入ください（500文字以内）"
                      className={`w-full px-4 py-2.5 rounded-xl border text-sm font-sans focus:outline-none resize-none transition-colors ${charCount > 500 ? "border-red-300 focus:border-red-400" : "border-stone-200 focus:border-teal"}`}
                    />
                    <div className="flex justify-between mt-1">
                      <p className="text-xs text-charcoal/30 font-sans">※このフォームは直接運営チームに届きます</p>
                      <p className={`text-xs font-sans ${charCount > 500 ? "text-red-400" : "text-charcoal/30"}`}>{charCount}/500</p>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || !form.topic || charCount > 500}
                    className="w-full bg-deep-green text-white py-3.5 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60 whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {submitting ? <><i className="ri-loader-4-line animate-spin" />送信中...</> : "送信する →"}
                  </button>
                </form>
              </div>
            )}

            {/* FAQ hint */}
            <div className="mt-8 bg-white rounded-2xl border border-stone-100 p-6">
              <h3 className="font-serif text-deep-green text-base mb-3 flex items-center gap-2">
                <i className="ri-question-line text-gold" />よくある質問
              </h3>
              <div className="space-y-3">
                {[
                  { q: "購入した動画が見られない", a: "マイページの「学習中」タブから確認できます。改善しない場合はお問い合わせください。" },
                  { q: "パスワードを忘れた", a: "ログイン画面の「パスワードを忘れた方」からリセットできます。" },
                  { q: "出品方法がわからない", a: "事業者モードに切り替え、「出品する」から動画講座・教材キットを登録できます。" },
                ].map((item) => (
                  <div key={item.q} className="border-b border-stone-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm font-sans font-medium text-charcoal">{item.q}</p>
                    <p className="text-xs text-charcoal/50 font-sans mt-0.5 leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
